import { API_BASE } from '../config/apiBase';
const API = API_BASE;

export function isWebAuthnSupported() {
    return !!(window.PublicKeyCredential && navigator.credentials);
}

export async function isPlatformAuthenticatorAvailable() {
    if (!isWebAuthnSupported()) return false;
    try {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch { return false; }
}

function bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer);
    let str = '';
    bytes.forEach(b => str += String.fromCharCode(b));
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64urlToBuffer(base64url) {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
}

async function safeParse(response) {
    const text = await response.text();
    if (!text) return {};
    try { return JSON.parse(text); } catch (_e) { return {}; }
}

export async function registerBiometric(token) {
    const res = await fetch(`${API}/api/auth/webauthn/register/options`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Falha ao obter opcoes de registro');
    const options = await safeParse(res);

    options.challenge = base64urlToBuffer(options.challenge);
    options.user.id = base64urlToBuffer(options.user.id);
    if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({
            ...c, id: base64urlToBuffer(c.id)
        }));
    }

    const credential = await navigator.credentials.create({ publicKey: options });

    const verifyBody = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
            attestationObject: bufferToBase64url(credential.response.attestationObject),
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        },
    };
    if (credential.response.getTransports) {
        verifyBody.response.transports = credential.response.getTransports();
    }

    const verifyRes = await fetch(`${API}/api/auth/webauthn/register/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(verifyBody),
    });
    const verifyData = await safeParse(verifyRes);
    if (!verifyRes.ok) {
        throw new Error(verifyData.detail || 'Falha na verificacao');
    }
    return verifyData;
}

export async function loginWithBiometric(email) {
    const res = await fetch(`${API}/api/auth/webauthn/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    const optionsData = await safeParse(res);
    if (!res.ok) {
        throw new Error(optionsData.detail || 'Biometria nao disponivel');
    }
    const options = optionsData;

    options.challenge = base64urlToBuffer(options.challenge);
    if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(c => ({
            ...c, id: base64urlToBuffer(c.id)
        }));
    }

    const assertion = await navigator.credentials.get({ publicKey: options });

    const credential = {
        id: assertion.id,
        rawId: bufferToBase64url(assertion.rawId),
        type: assertion.type,
        response: {
            authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
            clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
            signature: bufferToBase64url(assertion.response.signature),
        },
    };
    if (assertion.response.userHandle) {
        credential.response.userHandle = bufferToBase64url(assertion.response.userHandle);
    }

    const verifyRes = await fetch(`${API}/api/auth/webauthn/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, credential }),
    });
    const verifyData = await safeParse(verifyRes);
    if (!verifyRes.ok) {
        throw new Error(verifyData.detail || 'Autenticacao biometrica falhou');
    }
    return verifyData;
}

export async function checkBiometricAvailable(email) {
    try {
        const res = await fetch(`${API}/api/auth/webauthn/check/${encodeURIComponent(email)}`);
        if (!res.ok) return false;
        const data = await safeParse(res);
        return data.available;
    } catch { return false; }
}
